import { useNotificationHandler } from "hooks/useNotificationHandler";
import { QUERY_KEYS } from "library/shared";
import { IUserSettings } from "library/types";
import {
  UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import api from "service/api";
import { useEffect } from "react";

interface Params {
  appId: number;
  enabled?: boolean;
}

export const getUserAppSettings = async ({
  appId,
}: Params): Promise<IUserSettings[]> => {
  // console.log("Making API call to fetch app settings for appId:", appId);
  const response = await api.get(`/uad/appsettings/${appId}`);
  // console.log("App settings API response:", response.data);
  return response.data as IUserSettings[];
};

export function useGetUserAppSettings({
  appId,
  enabled = true,
}: Params): UseQueryResult<IUserSettings[]> {
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  // console.log("useGetUserAppSettings hook called with:", { appId, enabled });

  const result = useQuery<IUserSettings[], Error>({
    queryKey: [QUERY_KEYS.userSettings, appId],
    queryFn: () => getUserAppSettings({ appId }),
    enabled: enabled && !!appId, // Make sure appId exists and is truthy
    staleTime: Infinity, // App settings rarely change - never consider stale
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    refetchOnMount: false, // Don't refetch if data is already in cache
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  useEffect(() => {
    if (result.error) {
      // console.error("Error fetching app settings:", result.error);
      handleAddAxiosErrorNotification(result.error);
    }
  }, [result.error, handleAddAxiosErrorNotification]);

  return result;
}

export const useInvalidateUserAppSettings = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.userSettings],
    });
};
